<template>
    <ModalBody>
        <CategoryManageContainer>
            <!-- * 왼쪽: 카테고리 추가 -->
            <CategoryAddSection>
                <h3>새로운 카테고리 추가</h3>
                <FormGroup>
                    <Label>카테고리1 이름</Label>
                    <Input v-model="category1Name" />
                    <ValidationMessage v-if="category1Message" :valid="category1Valid">
                        {{ category1Message }}
                    </ValidationMessage>
                </FormGroup>
                <FormGroup>
                    <Label>카테고리2 이름</Label>
                    <Input v-model="category2Name" />
                    <ValidationMessage v-if="category2Message" :valid="category2Valid">
                        {{ category2Message }}
                    </ValidationMessage>
                </FormGroup>
                <ActionButton @click="createCategory"
                    :disabled="!category1Valid || !category2Valid || !category1Name || !category2Name">
                    카테고리 추가
                </ActionButton>
            </CategoryAddSection>

            <!-- * 오른쪽: 카테고리 설정 -->
            <CategorySettingsSection>
                <CategoryTree>
                    <TreeHeader>
                        <h3>기존 카테고리 구조</h3>
                    </TreeHeader>

                    <!-- * 카테고리1 목록 -->
                    <div v-for="cat1 in allCategories" :key="cat1.num">
                        <CategoryItem :level="1">
                            <CategoryHeader>
                                <CategoryInfo>
                                    <ChevronButton :expanded="expandedCategories.includes(cat1.num)"
                                        @click="toggleCategory(cat1.num)">
                                        <ChevronIcon :size="30" />
                                    </ChevronButton>
                                    <!-- 수정 모드일 때는 input, 아닐 때는 텍스트 표시 -->
                                    <InlineEditWrapper v-if="editingCategory?.num === cat1.num">
                                        <InlineInput v-model="editingName" placeholder="카테고리 이름"
                                            @keyup.enter="saveEdit(1)" @keyup.esc="cancelEdit(1)"
                                            :inputRef="input => { if (input) editInputRef = input }" />
                                        <InlineActions>
                                            <SaveButton @click="saveEdit(1)">
                                                <CheckIcon :size="20" />
                                            </SaveButton>
                                            <CancelButton @click="cancelEdit(1)">
                                                <CloseIcon :size="20" />
                                            </CancelButton>
                                        </InlineActions>
                                    </InlineEditWrapper>
                                    <CategoryName v-else>{{ cat1.name }}</CategoryName>
                                </CategoryInfo>
                                <CategoryActions>
                                    <IconButton @click="startEdit(1, cat1)" v-if="editingCategory?.num !== cat1.num">
                                        <EditIcon :size="20" />
                                    </IconButton>
                                    <IconButton variant="delete" @click="confirmDeleteCategory(1, cat1.num)">
                                        <Close :size="20" />
                                    </IconButton>
                                </CategoryActions>
                            </CategoryHeader>

                            <!-- 카테고리2 목록 -->
                            <SubCategories v-if="expandedCategories.includes(cat1.num)">
                                <CategoryItem v-for="subcat in cat1.subcategories" :key="subcat.num" :level="2">
                                    <CategoryHeader>
                                        <CategoryInfo>
                                            <InlineEditWrapper v-if="editingSubCategory?.num === subcat.num">
                                                <InlineInput v-model="editingSubName" placeholder="카테고리 이름"
                                                    @keyup.enter="saveEdit(2)" @keyup.esc="cancelEdit(2)"
                                                    :inputRef="input => { if (input) editInputRef = input }" />
                                                <InlineActions>
                                                    <SaveButton @click="saveEdit(2)">
                                                        <CheckIcon :size="20" />
                                                    </SaveButton>
                                                    <CancelButton @click="cancelEdit(2)">
                                                        <CloseIcon :size="20" />
                                                    </CancelButton>
                                                </InlineActions>
                                            </InlineEditWrapper>
                                            <CategoryName v-else>{{ subcat.name }}</CategoryName>
                                        </CategoryInfo>
                                        <CategoryActions>
                                            <IconButton @click="startEdit(2, subcat, cat1.num)"
                                                v-if="editingCategory?.num !== subcat.num">
                                                <EditIcon :size="20" />
                                            </IconButton>
                                            <IconButton variant="delete" @click="confirmDeleteCategory(2, subcat.num)">
                                                <Close :size="20" />
                                            </IconButton>
                                        </CategoryActions>
                                    </CategoryHeader>
                                </CategoryItem>
                            </SubCategories>
                        </CategoryItem>
                    </div>
                </CategoryTree>
            </CategorySettingsSection>
        </CategoryManageContainer>
    </ModalBody>
</template>

<script>
import * as CategoryModalCss from '@/assets/styles/product/modal/CategoryModalCss.js';
import { debounce } from 'lodash';

import Close from 'vue-material-design-icons/Close.vue';
import EditIcon from 'vue-material-design-icons/Pencil.vue';
import ChevronIcon from 'vue-material-design-icons/ChevronDown.vue';
import CheckIcon from 'vue-material-design-icons/Check.vue';
import CloseIcon from 'vue-material-design-icons/Close.vue';

export default {
    name: 'CategoryModal',

    components: {
        ...CategoryModalCss,
        Close, EditIcon, ChevronIcon, CheckIcon, CloseIcon
    },

    data() {
        return {
            setName: '',
            availableProducts: [],
            selectedCategory1: '',
            selectedCategory2: '',
            isActive: true,
            category1List: [],
            category2List: [],
            category1Name: '',
            category2Name: '',
            editingName: '',
            editingCategoryLevel: null,
            editingParentId: null,

            // setup에서 옮긴 속성들
            allCategories: [],
            expandedCategories: [],
            showEditModal: false,
            editingCategory: null,
            editingSubCategory: null, // 서브카테고리 수정을 위한 별도 상태 추가
            editingSubName: '', // 서브카테고리 이름 수정을 위한 별도 상태 추가
            editInputRef: null,

            // 카테고리 추가 유효성 검사
            category1Valid: false,
            category2Valid: false,
            category1Message: '',
            category2Message: '',

            // 세트 관리
            setRows: Array(10).fill().map(() => ({
                category1: '',
                category2: '',
                category2List: []
            })),
        }
    },

    async created() {
        await this.loadInitialData();

        this.debouncedValidateCategory1 = debounce(this.validateCategory1, 300);
        this.debouncedValidateCategory2 = debounce(this.validateCategory2, 300);
    },

    directives: {
        focus: {
            mounted(el, binding) {
                if (binding.value) {
                    setTimeout(() => {
                        el.focus();
                    }, 100);
                }
            },
            updated(el, binding) {
                if (binding.value) {
                    setTimeout(() => {
                        el.focus();
                    }, 100);
                }
            }
        }
    },

    methods: {
        // 초기 데이터 로드 ----------------------------------------------------
        async loadInitialData() {
            try {
                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/category/level1`,
                    {
                        params: { companykey: this.$globalState.company_key }
                    }
                );
                if (response.data.success) {
                    this.category1List = response.data.data;
                }
            } catch (error) {
                console.error('데이터 로드 실패:', error);
            }
        },

        async createSet() {
            if (!this.setName.trim()) {
                this.$swal.fire({
                    title: '입력 오류',
                    text: '세트명을 입력해주세요.',
                    icon: 'warning'
                });
                return;
            }

            const validRows = this.setRows.filter(row => row.category1 && row.category2);
            if (validRows.length === 0) {
                this.$swal.fire({
                    title: '입력 오류',
                    text: '최소 하나의 카테고리를 선택해주세요.',
                    icon: 'warning'
                });
                return;
            }

            try {
                const response = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/createSet`,
                    {
                        name: this.setName,
                        categories: validRows.map(row => ({
                            category1_id: row.category1,
                            category2_id: row.category2
                        })),
                        companykey: this.$globalState.company_key
                    }
                );

                if (response.data.success) {
                    this.$emit('set-created', response.data.data);
                    this.$emit('close');
                    this.$swal.fire({
                        title: '성공',
                        text: '세트가 생성되었습니다.',
                        icon: 'success'
                    });
                }
            } catch (error) {
                console.error('세트 생성 실패:', error);
                this.$swal.fire({
                    title: '오류',
                    text: '세트 생성 중 오류가 발생했습니다.',
                    icon: 'error'
                });
            }
        },

        async validateCategory1() {
            if (!this.category1Name.trim()) {
                this.category1Valid = false;
                this.category1Message = '';
                return;
            }

            try {
                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/checkCategory`,
                    {
                        params: {
                            category_level: 1,
                            category_name: this.category1Name.trim(),
                            companykey: this.$globalState.company_key
                        }
                    }
                );

                if (response.data.exists) {
                    this.category1Valid = true;
                    this.category1Message = '이미 존재하는 카테고리1 입니다.';
                } else {
                    this.category1Valid = true;
                    this.category1Message = '사용 가능한 카테고리1 입니다.';
                }
            } catch (error) {
                console.error('카테고리1 검증 실패:', error);
                this.category1Valid = false;
                this.category1Message = '카테고리 확인 중 오류가 발생했습니다.';
            }
        },

        async validateCategory2() {
            if (!this.category2Name.trim()) {
                this.category2Valid = false;
                this.category2Message = '';
                return;
            }

            try {
                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/checkCategory`,
                    {
                        params: {
                            category_level: 2,
                            category_name: this.category2Name.trim(),
                            companykey: this.$globalState.company_key
                        }
                    }
                );

                if (response.data.exists) {
                    this.category2Valid = false;
                    this.category2Message = '이미 존재하는 카테고리2 입니다.';
                } else {
                    this.category2Valid = true;
                    this.category2Message = '사용 가능한 카테고리2 입니다.';
                }
            } catch (error) {
                console.error('카테고리2 검증 실패:', error);
                this.category2Valid = false;
                this.category2Message = '카테고리 확인 중 오류가 발생했습니다.';
            }
        },

        startEdit(level, category, parentId = null) {
            if (level === 1) {
                this.editingCategory = category;
                this.editingName = category.name;
            } else {
                this.editingSubCategory = category;
                this.editingSubName = category.name;
            }
            this.editingParentId = parentId;

            this.$nextTick(() => {
                if (this.editInputRef) {
                    this.editInputRef.focus();
                }
            });
        },

        async saveEdit(level) {
            const editingName = level === 1 ? this.editingName : this.editingSubName;
            const category = level === 1 ? this.editingCategory : this.editingSubCategory;

            if (!editingName.trim()) {
                this.$swal.fire({
                    title: '오류',
                    text: '카테고리 이름을 입력해주세요.',
                    icon: 'warning'
                });
                return;
            }

            try {
                const response = await this.$axios.put(
                    `${this.$apiUrls.hanqmedi}/updateCategory`,
                    {
                        category_level: level,
                        category_id: category.num,
                        parent_id: this.editingParentId,
                        new_name: editingName.trim(),
                        companykey: this.$globalState.company_key
                    }
                );

                if (response.data.success) {
                    await this.loadAllCategories();
                    this.cancelEdit(level);

                    this.$swal.fire({
                        title: '성공',
                        text: '카테고리가 수정되었습니다.',
                        icon: 'success'
                    });
                }
            } catch (error) {
                console.error('카테고리 수정 실패:', error);
                this.$swal.fire({
                    title: '오류',
                    text: error.response?.data?.message || '카테고리 수정 중 오류가 발생했습니다.',
                    icon: 'error'
                });
            }
        },

        cancelEdit(level) {
            if (level === 1) {
                this.editingCategory = null;
                this.editingName = '';
            } else {
                this.editingSubCategory = null;
                this.editingSubName = '';
            }
            this.editingParentId = null;
        },

        async loadAllCategories() {
            try {
                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/getAllCategories`,
                    {
                        params: {
                            companykey: this.$globalState.company_key
                        }
                    }
                );

                if (response.data.success) {
                    // API 응답에서 data 배열을 allCategories에 할당
                    this.allCategories = response.data.data.map(category => ({
                        num: category.num,
                        name: category.name,
                        subcategories: Array.isArray(category.subcategories) ? category.subcategories.map(subcat => ({
                            num: subcat.num,
                            name: subcat.name
                        })) : []
                    }));

                    // 디버깅을 위한 로그
                    console.log('로드된 카테고리:', this.allCategories);
                }
            } catch (error) {
                console.error('카테고리 로드 실패:', error);
                this.$swal.fire({
                    title: '오류',
                    text: '카테고리 목록을 불러오는데 실패했습니다.',
                    icon: 'error'
                });
            }
        },

        toggleCategory(categoryId) {
            const index = this.expandedCategories.indexOf(categoryId);
            if (index === -1) {
                this.expandedCategories.push(categoryId);
            } else {
                this.expandedCategories.splice(index, 1);
            }
        },

        async confirmDeleteCategory(level, categoryId) {
            const result = await this.$swal.fire({
                title: '카테고리 삭제',
                text: level === 1 ?
                    '하위 카테고리도 모두 삭제됩니다. 계속하시겠습니까?' :
                    '이 카테고리를 삭제하시겠습니까?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: '삭제',
                cancelButtonText: '취소',
                confirmButtonColor: '#dc3545'
            });

            if (result.isConfirmed) {
                await this.deleteCategory(level, categoryId);
            }
        },

        async deleteCategory(level, categoryId) {
            try {
                const response = await this.$axios.delete(
                    `${this.$apiUrls.hanqmedi}/deleteCategory`,
                    {
                        data: {
                            category_level: level,
                            category_id: categoryId,
                            companykey: this.$globalState.company_key
                        }
                    }
                );

                if (response.data.success) {
                    await this.loadAllCategories();
                    this.$swal.fire({
                        title: '성공',
                        text: '카테고리가 삭제되었습니다.',
                        icon: 'success'
                    });
                }
            } catch (error) {
                console.error('카테고리 삭제 실패:', error);
                this.$swal.fire({
                    title: '오류',
                    text: '카테고리 삭제 중 오류가 발생했습니다.',
                    icon: 'error'
                });
            }
        },

        // 카테고리 설정 ----------------------------------------------------
        async updateCategory() {
            if (!this.selectedCategory1 || !this.selectedCategory2) {
                this.$swal.fire({
                    title: '입력 오류',
                    text: '카테고리를 선택해주세요.',
                    icon: 'warning'
                });
                return;
            }

            try {
                const response = await this.$axios.put(
                    `${this.$apiUrls.hanqmedi}/category/update`,
                    {
                        category1_id: this.selectedCategory1,
                        category2_id: this.selectedCategory2,
                        is_active: this.isActive,
                        companykey: this.$globalState.company_key
                    }
                );

                if (response.data.success) {
                    this.$emit('category-updated', response.data.data);
                    this.$emit('close');
                    this.$swal.fire({
                        title: '성공',
                        text: '카테고리 설정이 업데이트되었습니다.',
                        icon: 'success'
                    });
                }
            } catch (error) {
                console.error('카테고리 업데이트 실패:', error);
                this.$swal.fire({
                    title: '오류',
                    text: '카테고리 업데이트 중 오류가 발생했습니다.',
                    icon: 'error'
                });
            }
        },

        // 카테고리 추가 ----------------------------------------------------
        async createCategory() {
            if (!this.category1Name || !this.category2Name) {
                this.$swal.fire({
                    title: '입력 오류',
                    text: '카테고리 이름을 입력해주세요.',
                    icon: 'warning'
                });
                return;
            }

            try {
                const response = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/createCategory`,
                    {
                        category1_name: this.category1Name,
                        category2_name: this.category2Name,
                        companykey: this.$globalState.company_key
                    }
                );

                if (response.data.success) {
                    await this.$swal.fire({
                        title: '성공',
                        text: '새 카테고리가 추가되었습니다.',
                        icon: 'success',
                    });

                    // 부모 컴포넌트에 카테고리 추가 이벤트 발생
                    this.$emit('category-added', {
                        category1Id: response.data.category1Id,
                        category2Id: response.data.category2Id
                    });

                    // 모달 닫기
                    this.$emit('close');

                    // 입력 필드 초기화
                    this.category1Name = '';
                    this.category2Name = '';
                } else {
                    throw new Error(response.data.message);
                }
            } catch (error) {
                console.error('카테고리 추가 오류:', error);

                this.$swal.fire({
                    title: '알림',
                    text: '이미 존재하는 카테고리입니다.',
                    icon: 'warning',
                });
            }
        },

        handleEscKey(event) {
            if (event.key === "Escape") {
                // 이벤트를 즉시 차단
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();

                // 다른 모든 ESC 키 핸들러가 실행되지 않도록 추가 차단
                if (event.cancelable) {
                    event.returnValue = false;
                }

                this.$emit("close");
                return false;
            }
        },
    },

    mounted() {
        this.loadAllCategories();

        document.addEventListener('keydown', this.handleEscKey, true);
    },

    beforeUnmount() {
        document.removeEventListener('keydown', this.handleEscKey, true);
    },

    watch: {
        category1Name(newVal) {
            if (!newVal.trim()) {
                this.category1Valid = false;
                this.category1Message = '';
            } else {
                this.debouncedValidateCategory1();
            }
        },
        category2Name(newVal) {
            if (!newVal.trim()) {
                this.category2Valid = false;
                this.category2Message = '';
            } else {
                this.debouncedValidateCategory2();
            }
        }
    }
}
</script>